import { useState, useEffect } from 'react';
import useGallery from './useGallery';
import useGalleryBehaviors from './useGalleryBehvaiors';

export default function useSessionGalleryInteractions(
    photoSessionId: string,
    galleryId: number,
) {
    const [isLoading, setIsLoading] = useState(true);
    const [photos, setPhotos] = useState<MediaInstance[]>([]);
    const [delayed, setDelayed] = useState(false);
    const [gallery, setGallery] = useState<GalleryObject | null>(null);
    const [loadedPhotos, setLoadedPhotos] = useState<MediaInstance[]>([]);
    const [expandedIndex, setExpandedIndex] = useState(-1);

    const { getSessionGallery, getGalleryById } = useGallery({
        slug: '',
        galleryId,
    });

    const getNewPage = (count: number) => {
        if (
            photos.length === loadedPhotos.length &&
            loadedPhotos.length !== 0
        ) {
            return;
        }

        setLoadedPhotos([
            ...loadedPhotos,
            ...photos.slice(
                loadedPhotos.length - 1,
                loadedPhotos.length + count - 1,
            ),
        ]);
    };

    const advance = () => {
        if (expandedIndex === loadedPhotos.length - 1) {
            getNewPage(10);
        }
        if (expandedIndex < photos.length) {
            setExpandedIndex(expandedIndex + 1);
        } else {
            setExpandedIndex(-1);
        }
    };

    const reverse = () => {
        setExpandedIndex(expandedIndex >= 0 ? expandedIndex - 1 : -1);
    };

    const { swipeHandlers } = useGalleryBehaviors(
        setExpandedIndex,
        advance,
        reverse,
    );

    useEffect(() => {
        // Skip the white-label redirect in dev so baseUrl galleries can be
        // tested against the local build
        if (gallery?.baseUrl && process.env.NODE_ENV !== 'development') {
            window.location.href = `${gallery.baseUrl}${window.location.pathname}`;
        }
    }, [gallery]);

    useEffect(() => {
        if (!isLoading) {
            setTimeout(() => setDelayed(true), 100);
        }
    }, [isLoading]);

    useEffect(() => {
        if (loadedPhotos.length === 0) {
            setLoadedPhotos([
                ...photos.slice(0, window?.outerWidth > 1500 ? 20 : 10),
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos.length]);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const gallery = await getGalleryById();
                const photos = await getSessionGallery(photoSessionId);

                setGallery(gallery);
                setPhotos(photos);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galleryId, photoSessionId]);

    return {
        photos,
        isLoading,
        gallery,
        swipeHandlers,
        expandedIndex,
        setExpandedIndex,
        delayed,
        loadedPhotos,
        getNewPage,
    };
}
